import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReportDimensions } from '../../../hooks/useReportDimensions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { List } from 'react-window';

const SCROLLBAR_SIZE = 10;
const COLUMN_WIDTH_MULTIPLIER = 6;
const GROUP_WIDTH = 24;

function HeaderRow({ style, tableColumns, addWidthToLastColumn }) {
  return (
    <div style={{ ...style, width: undefined, display: 'flex' }}>
      {Object.values(tableColumns).map((col, colIdx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={colIdx}
          style={{
            minWidth:
              col.width * COLUMN_WIDTH_MULTIPLIER +
              (Object.values(tableColumns).length - 1 === colIdx && addWidthToLastColumn
                ? SCROLLBAR_SIZE
                : 0),
            backgroundColor: col.headerBgColor,
          }}
          className={classNames(
            'bo-table-border bo-table-cell bo-table-row-bg-yellow',
            'fw-bold px-1 overflow-hidden',
          )}
        >
          {col.header}
        </div>
      ))}
    </div>
  );
}
function GroupRow({ index, style, transformedCollapsibleRows, toggleCollapsible }) {
  return (
    <div style={style} className="bo-table-group-cell">
      {transformedCollapsibleRows[index] && (
        <button
          type="button"
          className="h-100 w-100 border-0 bg-transparent"
          onClick={() => toggleCollapsible(index)}
        >
          <FontAwesomeIcon
            icon={transformedCollapsibleRows[index].collapsed ? faAngleRight : faAngleDown}
            color="#000"
            size="xs"
          />
        </button>
      )}
    </div>
  );
}

function Row({
  index,
  style,
  transformedReport,
  tableColumns,
  boldTotalRow,
  formatCellConditionally,
}) {
  if (!transformedReport || !tableColumns) {
    return null;
  }

  const cellClassName = 'bo-table-border bo-table-cell px-1 overflow-hidden';

  return (
    <div style={{ ...style, width: undefined, display: 'flex' }} className="bo-table-row-hover">
      {Object.entries(tableColumns).map(([columnKey, col]) => {
        const value = transformedReport[index][columnKey];
        const {
          numFmt,
          alignment: { horizontal: horizontalAlignment },
        } = col.style;

        let className = classNames(
          cellClassName,
          boldTotalRow && index === transformedReport.length - 1 ? 'fw-bold' : '',
          horizontalAlignment === 'left'
            ? 'text-start justify-content-start'
            : 'text-end justify-content-end',
          numFmt === '"$"#,##0.00;[Red]-"$"#,##0.00' && Array.from(value)[0] === '-'
            ? 'text-danger'
            : '',
        );

        if (formatCellConditionally) {
          const formattedCell = formatCellConditionally({
            reportItem: transformedReport[index],
            tableColumnKey: columnKey,
          });

          const alignments = [
            'justify-content-start',
            'justify-content-end',
            'text-start',
            'text-end',
          ];

          if (alignments.some(x => formattedCell.includes(x))) {
            alignments.forEach(x => {
              className = className.replace(x, '');
            });
          }

          className += formattedCell;
        }

        return (
          <div
            key={columnKey}
            className={className}
            style={{ width: col.width * COLUMN_WIDTH_MULTIPLIER }}
          >
            {value}
          </div>
        );
      })}
    </div>
  );
}

function ReportList({
  report,
  tableColumns,
  rowHeight = 36,
  getRowHeight,
  headerHeight = 52,
  boldTotalRow,
  overscanCount = 5,
  groups,
  formatCellConditionally,
}) {
  const { height, width } = useReportDimensions();

  const [transformedReport, setTransformedReport] = useState([]);
  const [collapsibleRows, setCollapsibleRows] = useState([]);

  const outerContainerRef = useRef(null);
  const headerGridRef = useRef(null);
  const groupGridRef = useRef(null);
  const mainGridRef = useRef(null);

  const transformedCollapsibleRows = useMemo(
    () =>
      transformedReport.map((_, transformedReportIndex) => {
        let count = 0;

        const collapsible = collapsibleRows.find(c => {
          if (c.parentIndex - count === transformedReportIndex) {
            return true;
          }

          if (c.collapsed) {
            count += c.childIndices.length;
          }

          return false;
        });

        if (collapsible) {
          return { collapsed: collapsible.collapsed };
        }

        return null;
      }),
    [transformedReport, collapsibleRows],
  );

  useEffect(() => {
    if (groups) {
      setCollapsibleRows(
        groups.map(g => ({
          parentIndex: g.parentIndex,
          childIndices: g.childIndices,
          collapsed: true,
        })),
      );
    } else {
      setTransformedReport(report);
    }
  }, [report, groups]);

  useEffect(() => {
    setTransformedReport(
      report.filter(
        (_, idx) => !collapsibleRows.find(g => g.childIndices.includes(idx))?.collapsed,
      ),
    );
  }, [collapsibleRows, report]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const { current: outerGrid } = outerContainerRef;

    if (outerGrid) {
      const handler = e => {
        e.preventDefault();
        const { deltaX, deltaY } = e;

        const header = headerGridRef.current;
        const group = groupGridRef.current?.element;
        const grid = mainGridRef.current?.element;

        const hiddenHeightContainer = grid?.lastElementChild;
        const hiddenWidthContainer = grid?.firstElementChild;

        if (grid && hiddenHeightContainer && hiddenWidthContainer) {
          let { scrollLeft, scrollTop } = grid;

          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            const maxVScroll = hiddenHeightContainer.clientHeight - grid.clientHeight;

            scrollTop = scrollTop + deltaY < maxVScroll ? scrollTop + deltaY : maxVScroll;
          } else {
            const maxHScroll = hiddenWidthContainer.clientWidth - grid.clientWidth;

            scrollLeft = scrollLeft + deltaX < maxHScroll ? scrollLeft + deltaX : maxHScroll;
          }

          if (header) {
            header.scrollLeft = scrollLeft;
          }

          if (group) {
            group.scrollTop = scrollTop;
          }

          grid.scrollLeft = scrollLeft;
          grid.scrollTop = scrollTop;
        }
      };

      outerGrid.addEventListener('wheel', handler);

      return () => outerGrid.removeEventListener('wheel', handler);
    }
  }, []);

  const onListScroll = useCallback(e => {
    e.preventDefault();

    if (headerGridRef.current) {
      headerGridRef.current.scrollLeft = e.target.scrollLeft;
    }

    if (groupGridRef.current?.element) {
      groupGridRef.current.element.scrollTop = e.target.scrollTop;
    }
  }, []);

  const toggleCollapsible = useCallback(
    transformedReportIndex => {
      let count = 0;

      const collapsible = collapsibleRows.find(c => {
        if (c.parentIndex - count === transformedReportIndex) {
          return true;
        }

        if (c.collapsed) {
          count += c.childIndices.length;
        }

        return false;
      });

      if (collapsible) {
        setCollapsibleRows(prevRows =>
          prevRows.map(row =>
            row.parentIndex === collapsible.parentIndex
              ? { ...collapsible, collapsed: !collapsible.collapsed }
              : row,
          ),
        );
      }
    },
    [collapsibleRows],
  );

  const contentHeight = useMemo(
    () =>
      getRowHeight
        ? transformedReport.reduce((acc, _, idx) => getRowHeight(idx) + acc, 0)
        : transformedReport.length * rowHeight,
    [transformedReport, getRowHeight, rowHeight],
  );

  const contentWidth = useMemo(
    () =>
      Object.values(tableColumns).reduce(
        (acc, val) => acc + val.width * COLUMN_WIDTH_MULTIPLIER,
        0,
      ),
    [tableColumns],
  );

  const isVerticalScrollVisable = contentHeight + headerHeight > height;

  const mainGridWidth = useMemo(
    () => (contentWidth + SCROLLBAR_SIZE < width ? contentWidth + SCROLLBAR_SIZE : width),
    [contentWidth, width],
  );

  return (
    <div ref={outerContainerRef}>
      <div className="d-flex">
        {groups && <div style={{ height: headerHeight, width: GROUP_WIDTH }} />}
        <div
          ref={headerGridRef}
          style={{ height: headerHeight, width: mainGridWidth }}
          className="bo-table-header-container"
        >
          <HeaderRow
            style={{ height: headerHeight }}
            tableColumns={tableColumns}
            addWidthToLastColumn={isVerticalScrollVisable}
          />
        </div>
      </div>
      <div className="d-flex">
        {groups && (
          <List
            style={{
              height: isVerticalScrollVisable
                ? height - headerHeight - SCROLLBAR_SIZE
                : contentHeight,
              width: GROUP_WIDTH,
            }}
            listRef={groupGridRef}
            className="bo-table-group-container"
            rowComponent={GroupRow}
            rowCount={transformedReport.length}
            rowHeight={getRowHeight || rowHeight}
            rowProps={{ transformedCollapsibleRows, toggleCollapsible }}
            overscanCount={overscanCount}
          />
        )}
        <List
          style={{ height: height - headerHeight, width: mainGridWidth }}
          listRef={mainGridRef}
          className="bo-table-container"
          rowComponent={Row}
          rowCount={transformedReport.length}
          rowHeight={getRowHeight || rowHeight}
          rowProps={{ transformedReport, tableColumns, boldTotalRow, formatCellConditionally }}
          onScroll={onListScroll}
          overscanCount={overscanCount}
        />
      </div>
    </div>
  );
}

export default ReportList;
